/**
 * CJdropshipping vs AutoDS 子页面 — 页面常量（全量文案）
 * 路由：/cjdropshipping-vs-autods
 *
 * 【注意】
 * - AutoDS 是一个 dropshipping 自动化工具（产品导入、订单自动化、价格监控）
 * - 图片使用 CDN 路径（resources/egg/alternatives/），函数接受 cdnHost 参数
 * - TESTIMONIALS/SELLPOINTS/FIT/ADVANTAGES/BOTTOM_CTA 与主页相同，走公共组件
 */

/**
 * 【注意】图片 CDN 路径：${cdnHost}/resources/egg/alternatives/filename
 * 参照 odm/index.jsx 模式，接受 cdnHost 参数
 */
export function getAutoDSConstants(cdnHost) {
  const img = (name) => `${cdnHost || ''}/resources/egg/alternatives/${name}`;
  return {
    // ========== VS_HERO: 子页 Banner ==========
    VS_HERO: {
      eyebrow: 'AutoDS Alternative',
      title: 'CJdropshipping vs',
      titleHighlight: 'AutoDS',
      subtitle: 'Looking for a Better Dropshipping AutoDS Alternative?',
      desc: 'CJdropshipping brings product sourcing, fulfillment, warehousing, POD, custom packaging, and branding together to help you grow faster and scale smarter.',
      proofItems: ['Reliable sourcing', 'Quality inspection', 'Branding support'],
      ctaText: 'Try for free',
      ctaUrl: '/home',
      note: 'Built for sellers ready to grow beyond automation tools.',
      heroImage: img('hero_banner3.jpg'),
      topChipText: 'All-in-one supply chain support',
      bottomCard: {
        label: 'Why CJdropshipping',
        title: 'Beyond automation, built for real supply chain growth',
        desc: 'Reliable sourcing, quality inspection, faster fulfillment, and branding support — all in one operational workflow.',
      },
    },

    // ========== SELLPOINTS: 卖点标签 ==========
    SELLPOINTS: [
      'Product Sourcing',
      'Global Fulfillment',
      'Quality Inspection',
      'Custom Packaging',
      'Print on Demand',
      'Global Warehouses',
      'Dedicated Agent Support',
    ],

    // ========== VS_TABLE: 三列对比表 ==========
    VS_TABLE: {
      kicker: 'Platform Comparison',
      title: '<strong>CJdropshipping</strong> vs AutoDS: Core Feature Comparison',
      desc: 'CJdropshipping offers a more complete dropshipping solution, covering product sourcing, quality inspection, warehousing, fulfillment, global shipping, and branding support.',
      summaryCards: [
        {
          icon: '1',
          title: 'More than automation',
          desc: 'AutoDS helps with product imports and automation, CJdropshipping supports the full dropshipping workflow from sourcing to fulfillment.',
        },
        {
          icon: '2',
          title: 'Built for real operations',
          desc: 'CJdropshipping gives sellers sourcing, inspection, warehousing, shipping, POD, and packaging support in one platform.',
        },
        {
          icon: '3',
          title: 'Better for scaling',
          desc: 'CJdropshipping is stronger for sellers who want stable supply chains, faster fulfillment, and long-term brand growth.',
        },
      ],
      headerFeature: 'Feature',
      headerCompetitor: 'AutoDS',
      headerCj: 'CJdropshipping',
      rows: [
        {
          feature: 'Product Positioning',
          competitor: 'Dropshipping automation software',
          cj: 'Dropshipping sourcing and fulfillment platform',
        },
        {
          feature: 'Main Value',
          competitor: 'Product importing, price and stock monitoring, workflow automation',
          cj: 'Product sourcing, purchasing, warehousing, fulfillment, POD, branding',
        },
        {
          feature: 'Product Source',
          competitor: 'Mainly connects to external supplier platforms',
          cj: 'Supports sourcing and supplier resources',
        },
        {
          feature: 'Order Fulfillment',
          competitor: 'Supports automated order workflows, but products are sourced from external suppliers',
          cj: 'Supports order processing, packing, shipping, and logistics',
        },
        {
          feature: 'Price & Stock Monitoring',
          competitor: 'One of its core strengths',
          cj: 'More connected to platform-based sourcing and fulfillment',
        },
        {
          feature: 'Branding Support',
          competitor: 'Not the core focus',
          cj: 'Supports custom packaging, POD, and white label options',
        },
        {
          feature: 'Warehousing',
          competitor: 'Mainly software-based management',
          cj: 'Supports global warehouses and fulfillment resources',
        },
        {
          feature: 'Best For',
          competitor: 'Sellers focused on automation, product importing, and large listing management',
          cj: 'Sellers building long-term dropshipping, DTC, or branded ecommerce businesses',
        },
        {
          feature: 'Business Stage',
          competitor: 'Better for automation and operational efficiency',
          cj: 'From product testing to scalable growth',
        },
        {
          feature: 'Core Difference',
          competitor: 'Solves automation and management efficiency challenges',
          cj: 'Solves sourcing and fulfillment challenges',
        },
      ],
    },

    // ========== FIT: Who Should Choose CJdropshipping ==========
    FIT: {
      kicker: 'Seller Fit Guide',
      title: 'Who Should Choose <strong>CJdropshipping?</strong>',
      desc: 'CJdropshipping is a better fit for sellers who:',
      imageUrl: img('fit_who_should_choose.jpg'),
      badgeText: '✓ Best Fit for Growth Sellers',
      floatTitle: 'Brand-building ecommerce sellers',
      floatDesc: 'A stronger fit for merchants who want sourcing, fulfillment, branding, and long-term operational support.',
      items: [
        'Need reliable product sourcing',
        'Want fulfillment support for dropshipping orders',
        'Sell through Shopify, WooCommerce, TikTok Shop, or eBay',
        'Want to test or sell POD products',
        'Need custom packaging or branding services',
        'Want to improve shipping and customer experience',
        'Plan to move from marketplace reselling to brand-based ecommerce',
        'See dropshipping as a long-term business, not just short-term product testing',
      ],
      summaryTitle: 'Why CJdropshipping stands out',
      summaryDesc: 'CJdropshipping is ideal for sellers who want to go beyond automation. It helps you build a stronger business foundation through sourcing, inspection, fulfillment, and branding support.',
    },

    // ========== ADVANTAGES: 核心优势（与主页相同，走公共组件） ==========
    ADVANTAGES: {
      kicker: 'Competitive Advantages',
      title: 'How <strong>CJdropshipping</strong> beat the competition',
      desc: 'CJdropshipping is a full dropshipping supply chain partner. It helps sellers go beyond product listing with sourcing, fulfillment, shipping, branding, and dedicated support.',
      cards: [
        {
          num: '01',
          title: 'Full Dropshipping Supply Chain Support',
          desc: 'CJdropshipping covers more than listing. It supports product sourcing, procurement, warehousing, fulfillment, shipping, and after-sales workflows.',
          imageUrl: img('adv_supply_chain.jpg'),
        },
        {
          num: '02',
          title: 'Reliable Product Sourcing',
          desc: 'Sellers can submit sourcing requests and find products through CJdropshipping\'s supplier resources, making it easier to discover better products and improve margins.',
          imageUrl: img('adv_sourcing.jpg'),
        },
        {
          num: '03',
          title: 'Quality Inspection Before Shipping',
          desc: 'CJdropshipping helps inspect products and packages before shipment, reducing the risk of defects, complaints, refunds, and negative reviews.',
          imageUrl: img('adv_quality_inspection.jpg'),
        },
        {
          num: '04',
          title: 'Global Warehousing',
          desc: 'With warehouse support in different markets, CJdropshipping helps sellers store inventory closer to customers and improve fulfillment efficiency.',
          imageUrl: img('adv_warehousing.jpg'),
        },
        {
          num: '05',
          title: 'End-to-End Order Fulfillment',
          desc: 'CJdropshipping can handle order processing, picking, packing, shipping, tracking, and delivery support, helping sellers reduce manual work.',
          imageUrl: img('adv_fulfillment.jpg'),
        },
        {
          num: '06',
          title: 'Global Shipping Solutions',
          desc: 'CJdropshipping provides global logistics options to help sellers deliver products to customers more efficiently and reliably.',
          imageUrl: img('adv_shipping.jpg'),
        },
        {
          num: '07',
          title: 'Custom Packaging and Branding',
          desc: 'Sellers can create branded packaging, inserts, labels, and unboxing experiences, making it easier to build a real ecommerce brand.',
          imageUrl: img('adv_custom_packaging.jpg'),
        },
        {
          num: '08',
          title: 'Dedicated Agent Support',
          desc: 'CJdropshipping provides service support to help sellers with sourcing, fulfillment, logistics, and operational issues as their business grows.',
          imageUrl: img('adv_dedicated_support.jpg'),
        },
      ],
      footerCta: {
        text: 'CJdropshipping stands out because it gives sellers the operational support needed to source better, fulfill faster, brand professionally, and scale with more confidence.',
        ctaText: 'GET STARTED',
        ctaUrl: '/home',
      },
    },

    // ========== TESTIMONIALS: 买家评价（与主页相同） ==========
    TESTIMONIALS: {
      kicker: 'Real Seller Stories',
      title: 'Why Growing Sellers <strong>Stay with CJdropshipping</strong>',
      desc: 'Hear how ecommerce sellers use CJdropshipping to streamline sourcing, fulfillment, branding, and shipping while building stronger long-term businesses.',
      rows: [
        [
          {
            avatarUrl: img('32.jpeg'),
            name: 'Emily Carter',
            role: 'Shopify Store Owner',
            country: 'United States',
            text: 'CJdropshipping helped me simplify my fulfillment process. Instead of manually coordinating with multiple suppliers, I can manage sourcing, orders, and shipping in one place. It saves me a lot of time every week.',
          },
          {
            avatarUrl: img('12.jpeg'),
            name: 'Daniel Hughes',
            role: 'Ecommerce Entrepreneur',
            country: 'United Kingdom',
            text: 'As my store started growing, I needed more than just a product listing tool. CJdropshipping gave me access to sourcing, fulfillment, and packaging support, which made it easier to scale my business.',
          },
          {
            avatarUrl: img('47.jpeg'),
            name: 'Sofia Martinez',
            role: 'Dropshipping Seller',
            country: 'Spain',
            text: 'One of the biggest advantages for me is product sourcing. When I cannot find the right product, CJdropshipping helps me look for alternatives and connect with supply options that fit my store.',
          },
        ],
        [
          {
            avatarUrl: img('15.jpeg'),
            name: 'Liam Walker',
            role: 'DTC Brand Founder',
            country: 'Australia',
            text: 'I wanted my store to feel more professional, and CJdropshipping\'s custom packaging and branding options helped a lot. It made my customer experience feel less generic.',
          },
          {
            avatarUrl: img('5.jpeg'),
            name: 'Chloe Anderson',
            role: 'Online Store Operator',
            country: 'Canada',
            text: 'Managing products, orders, and fulfillment through one platform makes daily operations much easier. CJdropshipping is especially helpful if you want to reduce repetitive manual work.',
          },
          {
            avatarUrl: img('60.jpeg'),
            name: 'Marco Rossi',
            role: 'Ecommerce Seller',
            country: 'Italy',
            text: 'CJdropshipping feels like a better fit for sellers who want to build a serious dropshipping business. It is not only about listing products, but also about improving sourcing, shipping, and the overall customer experience.',
          },
        ],
      ],
    },

    // ========== VS_FAQ: 子页专属 FAQ ==========
    VS_FAQ: {
      kicker: 'FAQ',
      title: 'AutoDS vs <strong>CJdropshipping</strong> FAQ',
      intro:
        'Get quick answers about how CJdropshipping compares with AutoDS for sourcing, fulfillment, shipping, branding, and long-term dropshipping growth.',
      sideCard: {
        label: 'Quick Decision Guide',
        title: 'Need more than a dropshipping automation tool?',
        desc: 'AutoDS is useful for product imports and order automation, while CJdropshipping gives sellers more support across sourcing, inspection, fulfillment, logistics, packaging, and brand growth.',
        miniItems: [
          { text: 'Better for sourcing and fulfillment' },
          { text: 'Supports global shipping and warehousing' },
          { text: 'Helps sellers build branded customer experiences' },
        ],
      },
      items: [
        {
          q: 'Is CJdropshipping an AutoDS alternative?',
          a: 'Yes. CJdropshipping can be used as an AutoDS alternative if you need more than product importing and automation. It offers product sourcing, order fulfillment, global warehousing, POD, custom packaging, and branding support.',
        },
        {
          q: 'What is the main difference between CJdropshipping and AutoDS?',
          a: 'AutoDS focuses more on dropshipping automation, such as product importing, price monitoring, stock monitoring, and workflow rules. CJdropshipping focuses more on sourcing, fulfillment, warehousing, shipping, POD, and branding services.',
        },
        {
          q: 'Is AutoDS a supplier?',
          a: 'AutoDS is mainly known as an automation platform, not a traditional supplier or fulfillment provider. Sellers usually connect AutoDS with external supplier sources, while CJdropshipping provides stronger support for product sourcing and order fulfillment.',
        },
        {
          q: 'Why do sellers look for an AutoDS alternative?',
          a: 'Sellers may look for an AutoDS alternative when they need more control over product sourcing, better fulfillment support, custom packaging, global warehousing, or branding options instead of relying only on automation features.',
        },
        {
          q: 'Is CJdropshipping better than AutoDS for fulfillment?',
          a: 'CJdropshipping is more suitable for fulfillment-focused sellers because it supports product sourcing, order processing, packing, shipping, warehousing, and related dropshipping services.',
        },
        {
          q: 'Can I use CJdropshipping for product sourcing?',
          a: 'Yes. CJdropshipping supports product sourcing, helping sellers find products and supply options that match their store, niche, and target market.',
        },
        {
          q: 'Does CJdropshipping support custom packaging?',
          a: 'Yes. CJdropshipping supports custom packaging and branding services, which can help sellers create a more professional and memorable customer experience.',
        },
        {
          q: 'Who should choose CJdropshipping instead of AutoDS?',
          a: 'CJdropshipping is better for sellers who want to build a more complete dropshipping business with sourcing, fulfillment, warehousing, POD, custom packaging, and branding support. It is especially suitable for sellers who want to grow beyond basic automation.',
        },
        {
          q: 'Is CJdropshipping good for long-term dropshipping growth?',
          a: 'Yes. CJdropshipping is designed for sellers who want to move beyond simple product importing and build a more scalable business with better supply chain support, fulfillment services, and branding options.',
        },
      ],
      bottomText: 'Still have questions? Start with a free account and explore what CJdropshipping can do for your business.',
      bottomCtaText: 'Try CJdropshipping Free',
      bottomCtaUrl: '/home',
    },

    // ========== BOTTOM_CTA: 底部行动召唤 ==========
    BOTTOM_CTA: {
      kicker: 'Ready to Scale Smarter?',
      title: 'Ready to move from dropshipping automation to complete sourcing and fulfillment?',
      desc: 'Start with CJdropshipping and build a more scalable ecommerce business.',
      ctaText: 'Upgrade from AutoDS to CJdropshipping',
      ctaUrl: '/home',
      bgImage: img('hero_banner.jpg'),
    },
  };
}
